import { sdkCompatibilityError } from '@ndrc/app-sdk/manifest';
import { useCallback } from 'react';
import type { JSX } from 'react';
import { useNavigate, useParams } from 'react-router';
import { EmptyState } from '@ndrc/ui';
import { AppWindow } from '../window/AppWindow';
import { createClock } from '../lib/clock';
import { useDeskApps } from '../lib/queries';
import { t } from '../messages';
import { useDesk } from '../desk/context';
import { NoWorkspace } from '../desk/NoWorkspace';
import { findManifest, INSTALLED_APPS } from './registry';
import { buildAppContext } from './buildAppContext';
import { AppErrorBoundary } from './AppErrorBoundary';
import { StudentMount } from './StudentMount';

/**
 * L'hôte d'application : la fenêtre du cœur, et dedans, l'app.
 *
 * Quatre refus explicites avant de monter quoi que ce soit — chacun avec sa phrase, aucun écran
 * blanc : app inconnue, app non activée pour la classe, chargement en cours, espace de travail pas
 * encore prêt. Le cinquième filet, l'`AppErrorBoundary`, attrape ce qui casse **après** — une app
 * qui plante ne doit jamais emporter la barre de menu, le dock, ni le bouton pour la fermer.
 *
 * Rien ici ne nomme une app : identifiant, nom, route par défaut et permissions viennent tous du
 * manifeste ; l'activation vient de `desk.apps`.
 *
 * **Chacune de ces phrases est écrite pour celui qui la lit.** L'enseignant ouvre ces écrans en
 * permanence — pour montrer, pour préparer, pour comprendre — et y lisait « Prévenez votre
 * enseignant ». Le choix de la rédaction n'est pas fait ici : `t()` sert la variante `.teacher`
 * quand elle existe (`src/messages.ts`). Ce que ce fichier ajoute, c'est le **geste** : quand
 * l'adulte peut débloquer lui-même ce qui bloque, le bouton est à côté de la phrase.
 */

const clock = createClock();

export function AppHost(): JSX.Element {
  const { appId } = useParams();
  const navigate = useNavigate();
  const { me, classId, className, workspace, openTeacher, observing } = useDesk();
  const deskApps = useDeskApps(classId);

  const manifest = appId === undefined ? undefined : findManifest(appId);

  const navigateInApp = useCallback(
    (path: string) => {
      if (appId === undefined) return;
      void navigate(`/app/${appId}${path.startsWith('/') ? path : `/${path}`}`);
    },
    [appId, navigate],
  );

  const openApp = useCallback(
    (target: string, path?: string) => {
      void navigate(`/app/${target}${path ?? ''}`);
    },
    [navigate],
  );

  const title = manifest?.name ?? t('host.unknownAppTitle');

  const body = ((): JSX.Element => {
    if (appId === undefined || manifest === undefined) {
      return <EmptyState title={t('host.unknownAppTitle')} hint={t('host.unknownAppHint')} />;
    }
    const incompatible = sdkCompatibilityError(manifest.sdk);
    if (incompatible) return <EmptyState title={t('host.incompatible')} hint={incompatible} />;
    if (deskApps.data !== undefined && !deskApps.data.appIds.includes(appId)) {
      return (
        <EmptyState title={t('host.lockedTitle')} hint={t('host.lockedHint')}>
          {/* Le geste, pas seulement le constat — mais seulement pour qui peut l'accomplir : un
              élève ne verrait qu'un bouton qui lui serait refusé (`role: 'teacher'`). */}
          {me.role === 'teacher' ? (
            <button
              type="button"
              className="ndrc-button ndrc-button--primary"
              onClick={() => {
                openTeacher('apps');
              }}
            >
              {t('host.lockedAction')}
            </button>
          ) : null}
        </EmptyState>
      );
    }
    if ((manifest.workspace !== 'none' && workspace === undefined) || deskApps.data === undefined) {
      return <EmptyState title={t('host.loading', { name: manifest.name })} />;
    }
    if (workspace === null && manifest.workspace !== 'none') {
      return <NoWorkspace titleKey="host.noWorkspaceTitle" hintKey="host.noWorkspaceHint" />;
    }

    const ctx = buildAppContext({
      manifest,
      declaredPermissions: manifest.permissions.uses,
      enabledManifests: INSTALLED_APPS.filter((item) => deskApps.data?.appIds.includes(item.id)),
      me,
      klass:
        classId === undefined
          ? (me.classes[0] ?? null)
          : (me.classes.find((k) => k.id === classId) ?? null),
      workspace: manifest.workspace === 'none' ? null : (workspace ?? null),
      clock,
      navigate: navigateInApp,
      openApp,
      openEnvironments: () => {
        void navigate('/environments');
      },
      observing:
        observing === null ? null : { classId: observing.classId, userId: observing.userId },
    });

    /**
     * **`key` sur l'espace de travail : c'est ce qui fait basculer l'outil ouvert de l'élève quand
     * l'enseignant distribue un autre fichier.**
     *
     * Répétition générale du 7 septembre 2026 : après une distribution, le Tableur ouvert d'un
     * élève affichait encore « GreenTech — 59 lignes » cent quarante secondes plus tard, pendant
     * que l'écran de l'enseignant lui promettait « sans avoir à recharger leur page ». Le fil
     * temps réel (`lib/useRealtimeSignals.ts`) invalide bien `desk.resolveWorkspace`, et
     * `workspace.workspaceId` change donc en quelques secondes — mais une app qui a chargé sa
     * grille une fois au montage ne relit rien pour autant, et le cœur ne peut pas le lui demander :
     * il ne connaît aucune de ses requêtes.
     *
     * Ce que le cœur PEUT faire sans rien savoir d'elle, c'est la remonter. Changer la `key`
     * démonte l'app et la remonte sur le nouvel espace : elle repart de son propre chargement
     * initial, quel qu'il soit. Le mécanisme vaut pour toute app installée aujourd'hui ou demain,
     * sans qu'aucune ligne d'`apps/desktop` ne la nomme (règle 1 de `CLAUDE.md`), et sans qu'une
     * app ait à s'abonner à quoi que ce soit.
     *
     * Ce qui est perdu au passage — la pile « Annuler », un tri en cours, une cellule en cours de
     * saisie — appartenait au fichier PRÉCÉDENT : le garder aurait été plus trompeur que de le
     * perdre. Le module JavaScript de l'app, lui, reste en cache (`StudentMount`, `loaded`) : la
     * remontée ne retélécharge rien.
     *
     * `resetKey` de la frontière d'erreur suit la même paire : une app qui a planté sur l'ancien
     * fichier doit avoir sa chance sur le nouveau.
     */
    /**
     * **`generation` fait partie de la clé, depuis le chantier 9.** Une remise à zéro ne change pas
     * l'identifiant de l'espace — l'élève travaille toujours dans le même — mais elle en réécrit
     * tout le contenu et incrémente `core.workspaces.generation`. Sans elle dans cette clé, le
     * Tableur resté ouvert continuait d'afficher, et d'écrire, l'état d'avant : exactement le
     * défaut que le changement de fichier client avait déjà produit, un cran plus bas.
     *
     * Le fil temps réel apporte le signal (`core.workspace.reset`, publié sur le canal de CET
     * espace), le bureau relit `desk.resolveWorkspace`, la génération change, l'app se remonte.
     * Aucune app n'a rien à savoir de tout cela.
     */
    const mountKey = `${appId}:${manifest.workspace === 'none' ? classId : workspace?.workspaceId}:${String(manifest.workspace === 'none' ? 0 : (workspace?.generation ?? 0))}`;

    const mount = (
      <StudentMount
        key={mountKey}
        appId={appId}
        ctx={ctx}
        fallback={<EmptyState title={t('host.loading', { name: manifest.name })} />}
      />
    );

    /**
     * **Il doit savoir en permanence qu'il est en démonstration.** C'est projeté devant trente
     * personnes : confondre son espace de démonstration avec le fichier d'un élève ferait raconter
     * n'importe quoi à la classe. Le bandeau appartient au CŒUR, au-dessus de l'app, et ne dépend
     * d'aucune coopération de celle-ci — qui n'a rien à savoir de tout cela.
     *
     * L'enveloppe n'existe QUE dans ce cas : `.wbody` est un `flex` que l'app remplit, et lui
     * ajouter un frère pour tout le monde changerait la mise en page de chaque outil d'élève pour
     * un bandeau qu'aucun d'eux ne verra jamais. `.host-demo` reproduit exactement le contrat de
     * `.wbody` autour de l'app (`window.css`).
     */
    /**
     * **Deux bandeaux possibles, jamais les deux : ils disent des choses opposées.**
     *
     * « Démonstration » veut dire *ces données sont les vôtres, écrivez librement, c'est projeté*.
     * « Vous lisez le travail de X » veut dire *ces données ne sont pas les vôtres, vous ne pouvez
     * rien y écrire*. L'observation l'emporte, parce qu'un espace observé n'est jamais celui d'une
     * démonstration (`teacher.studentWorkspace` ne résout que `kind = 'student'`) — la condition
     * est donc exclusive par construction, pas par précaution.
     *
     * Ce bandeau-ci double celui du bureau (`desk/Desk.tsx`) et ce n'est pas une redite : celui du
     * bureau dit DE QUI, celui-ci dit que **cette fenêtre-là** est en lecture. Une app peut
     * afficher un bouton « Enregistrer » que le bureau ne connaît pas ; l'enseignant doit savoir,
     * sans le presser, ce qui va se passer.
     */
    const chrome =
      observing !== null
        ? { className: 'host-observe', text: t('observe.windowBanner') }
        : manifest.workspace !== 'none' && workspace?.kind === 'demo'
          ? { className: 'host-demo', text: t('host.demoBanner') }
          : null;

    return (
      <AppErrorBoundary resetKey={mountKey}>
        {chrome === null ? (
          mount
        ) : (
          <div className="host-demo">
            <p className={`host-demo-banner ${chrome.className}-banner`}>{chrome.text}</p>
            <div className="host-demo-body">{mount}</div>
          </div>
        )}
      </AppErrorBoundary>
    );
  })();

  return (
    <AppWindow title={title} {...(className === undefined ? {} : { subtitle: className })}>
      {body}
    </AppWindow>
  );
}
